import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'
import { useDashboardStats } from '../hooks/useDashboardStats'

interface DashboardHomeProps {
  onSwitchToDeals: () => void
  onSwitchToSubmit?: () => void
}

const DashboardHome: React.FC<DashboardHomeProps> = ({ onSwitchToDeals, onSwitchToSubmit }) => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { stats, loading: statsLoading, error: statsError } = useDashboardStats()

  const handleSubmitDeal = () => {
    if (onSwitchToSubmit) {
      onSwitchToSubmit()
    } else {
      navigate('/submit-deal')
    }
  }

  const handleViewDeals = () => {
    onSwitchToDeals()
  }

  const handleDownloadTemplate = (format: 'pdf' | 'docx') => {
    const endpoint = `/api/templates/download/${format}`
    const filename = format === 'pdf' 
      ? 'FM_Funding_Application_Template.pdf' 
      : 'FM_Funding_Application_Template.docx'
    
    // Create a temporary link to trigger download
    const link = document.createElement('a')
    link.href = endpoint
    link.download = filename
    link.style.display = 'none'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="iso-content-section">
      {/* Page Header */}
      <div className="iso-content-header">
        <h2 className="iso-page-title">Dashboard Overview</h2>
      </div>

      <div className="iso-content-body">
        {/* Welcome Section */}
        <div className="iso-welcome-section">
          <h2 className="iso-welcome-title text-white">Welcome to your Partner Portal</h2>
          
          
          {/* User Info */}
          <div className="row">
            <div className="col-md-6">
              <h6 className="text-white-50">Account Information</h6>
              <ul className="list-unstyled text-white">
                <li><strong>Email:</strong> {user?.email}</li>
                <li><strong>Role:</strong> <span className="badge bg-light text-primary">{user?.role?.toUpperCase()}</span></li>
                <li><strong>Status:</strong> <span className="badge bg-success">{user?.status?.toUpperCase()}</span></li>
              </ul>
            </div>
            <div className="col-md-6">
              <h6 className="text-white-50">Quick Stats</h6>
              {statsLoading ? (
                <div className="text-white">
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Loading stats...
                </div>
              ) : statsError ? (
                <div className="text-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  Unable to load stats
                </div>
              ) : (
                <ul className="list-unstyled text-white">
                  <li><strong>Total Opportunities:</strong> {stats.totalDeals}</li>
                  <li><strong>Pending:</strong> {stats.pendingSubmissions}</li>
                  <li><strong>Approved:</strong> {stats.approvedSubmissions}</li>
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Action Cards */}
        <div className="iso-action-cards">
          {/* Submit Deal Card */}
          <div className="iso-action-card">
            <div 
              className="iso-action-card-icon"
              style={{ 
                background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
                color: '#ffffff'
              }}
            >
              <span style={{ color: '#ffffff', fontSize: '2.5rem', fontWeight: 'bold' }}>📄</span>
            </div>
            <h4 className="iso-action-card-title">Submit New Deal</h4>
            <p className="iso-action-card-description">
              Create and submit a new funding application with our streamlined process and AI-powered document parsing.
            </p>
            <button 
              className="iso-action-card-button btn btn-primary"
              onClick={handleSubmitDeal}
            >
              <i className="bi bi-plus-circle me-2"></i>
              Start New Application
            </button>
          </div>

          {/* View Deals Card */}
          <div className="iso-action-card">
            <div 
              className="iso-action-card-icon"
              style={{ 
                background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                color: '#ffffff'
              }}
            >
              <span style={{ color: '#ffffff', fontSize: '2.5rem', fontWeight: 'bold' }}>📊</span>
            </div>
            <h4 className="iso-action-card-title">View Your Deals</h4>
            <p className="iso-action-card-description">
              Track all your submitted opportunities and monitor lender submissions and their approval status.
            </p>
            <button 
              className="iso-action-card-button btn btn-success"
              onClick={handleViewDeals}
            >
              <i className="bi bi-eye me-2"></i>
              View Your Deals
            </button>
          </div>

          {/* Download Templates Card */}
          <div className="iso-action-card">
            <div 
              className="iso-action-card-icon"
              style={{ 
                background: 'linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%)',
                color: '#ffffff'
              }}
            >
              <span style={{ color: '#ffffff', fontSize: '2.5rem', fontWeight: 'bold' }}>📥</span>
            </div>
            <h4 className="iso-action-card-title">Download App Templates</h4>
            <p className="iso-action-card-description">
              Choose between pdf or word doc templates to rebrand with your own logo and disclaimer
            </p>
            <div className="d-flex gap-2 flex-wrap">
              <button 
                className="iso-action-card-button btn btn-outline-info flex-fill"
                onClick={() => handleDownloadTemplate('pdf')}
              >
                <i className="bi bi-file-earmark-pdf me-2"></i>
                Download PDF
              </button>
              <button 
                className="iso-action-card-button btn btn-outline-info flex-fill"
                onClick={() => handleDownloadTemplate('docx')}
              >
                <i className="bi bi-file-earmark-word me-2"></i>
                Download Word
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardHome
